import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { DataTable, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui';
import type { Column } from '@/components/ui';
import type { TerminalErrorEntry, PosErrorLevel, Restaurant } from '@/types/api';
import {
  Search,
  Filter,
  Eye,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react';

const levelStyle: Record<PosErrorLevel, string> = {
  ERROR: 'bg-red-100 text-red-800',
  FATAL: 'bg-red-200 text-red-900 font-semibold',
};

const levelIcon: Record<PosErrorLevel, typeof AlertCircle> = {
  ERROR: AlertCircle,
  FATAL: AlertTriangle,
};

export default function ErrorLogPage() {
  const scope = useAuthStore((s) => s.scope);
  const canReadGroups = useHasPermission(PERM.GROUP_READ);
  const canReadRestaurants = useHasPermission(PERM.RESTAURANT_READ);
  // Mirrors the backend's errors.manage gate on PATCH /resolve (H1) -- errors.read
  // alone only grants visibility, not the ability to dismiss/triage an error.
  const canManageErrors = useHasPermission(PERM.ERRORS_MANAGE);

  const isPlatformOrMixed = scope?.type === 'PLATFORM' || scope?.type === 'MIXED';
  const showGroupFilter = canReadGroups || isPlatformOrMixed;
  const showRestaurantFilter = canReadRestaurants || scope?.type === 'GROUP' || isPlatformOrMixed;

  const [selected, setSelected] = useState<TerminalErrorEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    groupId?: number;
    restaurantId?: number;
    terminalId?: string;
    errorClass?: string;
    level?: PosErrorLevel;
    fromDate?: string;
    toDate?: string;
    resolved?: 'all' | 'unresolved' | 'resolved';
  }>({ resolved: 'unresolved' });
  const [errorsPageIndex, setErrorsPageIndex] = useState(0);
  const ERRORS_PAGE_SIZE = 50;

  // A filter change can leave errorsPageIndex pointing past the new result
  // set's last page (or just showing an unrelated page of a different scope)
  // -- reset to the first page whenever the filter set changes (H3).
  useEffect(() => {
    setErrorsPageIndex(0);
  }, [filters]);

  const queryClient = useQueryClient();

  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiClient.getGroups(),
    enabled: showGroupFilter,
    staleTime: 5 * 60 * 1000,
  });

  // Paginated with no picker-friendly "give me everything" option, so walk
  // every page instead of hardcoding a page size that would silently
  // truncate the dropdown past that many restaurants (M1).
  const { data: allRestaurants = [] } = useQuery<Restaurant[]>({
    queryKey: ['restaurants-all'],
    queryFn: async () => {
      const all: Restaurant[] = [];
      let page = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await apiClient.getRestaurants(page, 200);
        all.push(...result.content);
        if (result.last) break;
        page += 1;
      }
      return all;
    },
    enabled: showRestaurantFilter,
    staleTime: 5 * 60 * 1000,
  });

  const visibleRestaurants = useMemo(() => {
    if (filters.groupId != null) {
      return allRestaurants.filter((r) => r.groupId === filters.groupId);
    }
    return allRestaurants;
  }, [allRestaurants, filters.groupId]);

  const { data: errorsPage, isLoading, isError, refetch } = useQuery({
    queryKey: ['terminalErrors', filters, errorsPageIndex],
    queryFn: () =>
      apiClient.getTerminalErrors({
        restaurantId: filters.restaurantId,
        groupId: filters.groupId,
        terminalId: filters.terminalId || undefined,
        errorClass: filters.errorClass || undefined,
        level: filters.level,
        fromTs: filters.fromDate ? new Date(filters.fromDate).toISOString() : undefined,
        toTs: filters.toDate
          ? new Date(filters.toDate + 'T23:59:59').toISOString()
          : undefined,
        resolved:
          filters.resolved === 'all' || !filters.resolved
            ? undefined
            : filters.resolved === 'resolved',
        page: errorsPageIndex,
        size: ERRORS_PAGE_SIZE,
      }),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: number) => apiClient.markTerminalErrorResolved(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['terminalErrors'] });
      // Update the modal-bound entry locally so the user sees instant feedback.
      setSelected((prev) =>
        prev && prev.id === id ? { ...prev, resolved: true, resolvedAt: new Date().toISOString() } : prev
      );
    },
  });

  function handleGroupChange(groupId: number | undefined) {
    setFilters((prev) => ({ ...prev, groupId, restaurantId: undefined }));
  }

  const restaurantNameById = useMemo(() => {
    const m = new Map<number, string>();
    allRestaurants.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [allRestaurants]);

  const columns: Column<TerminalErrorEntry>[] = [
    {
      key: 'occurredAt',
      header: 'When',
      sortable: true,
      render: (e) => (
        <div className="text-sm">
          <span className="text-gray-900">{new Date(e.occurredAt).toLocaleTimeString()}</span>
          <span className="text-gray-500 block text-xs">
            {new Date(e.occurredAt).toLocaleDateString()}
          </span>
        </div>
      ),
    },
    {
      key: 'level',
      header: 'Level',
      render: (e) => {
        const Icon = levelIcon[e.level];
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${levelStyle[e.level]}`}
          >
            <Icon className="h-3 w-3" />
            {e.level}
          </span>
        );
      },
    },
    {
      key: 'restaurantId',
      header: 'Restaurant',
      render: (e) => (
        <span className="text-sm text-gray-900">
          {restaurantNameById.get(e.restaurantId) ?? `#${e.restaurantId}`}
        </span>
      ),
    },
    {
      key: 'terminalId',
      header: 'Terminal',
      render: (e) => <span className="text-sm font-mono text-gray-700">{e.terminalId}</span>,
    },
    {
      key: 'errorClass',
      header: 'Error',
      render: (e) => (
        <div className="text-sm min-w-0">
          <div className="font-medium text-gray-900 truncate" title={e.errorClass ?? ''}>
            {e.errorClass ?? '—'}
          </div>
          {e.message && (
            <div className="text-gray-500 text-xs truncate max-w-md" title={e.message}>
              {e.message}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'resolved',
      header: 'Status',
      render: (e) =>
        e.resolved ? (
          <span className="inline-flex items-center gap-1 text-green-700 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Resolved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
            <Clock className="h-3.5 w-3.5" />
            Open
          </span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (e) => (
        <button
          onClick={() => setSelected(e)}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="View details"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
    },
  ];

  const filteredData = errorsPage?.content?.filter((entry) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      entry.errorClass?.toLowerCase().includes(q) ||
      entry.message?.toLowerCase().includes(q) ||
      entry.terminalId?.toLowerCase().includes(q) ||
      entry.loggerClass?.toLowerCase().includes(q)
    );
  });

  const activeFilterCount = [
    filters.groupId,
    filters.restaurantId,
    filters.terminalId,
    filters.errorClass,
    filters.level,
    filters.fromDate,
    filters.toDate,
    filters.resolved && filters.resolved !== 'unresolved' ? filters.resolved : undefined,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">POS Errors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Runtime errors forwarded by POS terminals across the platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by error class, message, terminal, or logger..."
              className="input w-full pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-outline h-10 relative ${showFilters ? 'bg-gray-100' : ''}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-medium">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
            {(showGroupFilter || showRestaurantFilter) && (
              <div className="grid grid-cols-2 gap-4">
                {showGroupFilter && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                    <select
                      value={filters.groupId ?? ''}
                      onChange={(e) =>
                        handleGroupChange(e.target.value ? Number(e.target.value) : undefined)
                      }
                      className="input w-full"
                    >
                      <option value="">All Groups</option>
                      {(groups ?? []).map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showRestaurantFilter && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restaurant
                    </label>
                    <select
                      value={filters.restaurantId ?? ''}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          restaurantId: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                      className="input w-full"
                    >
                      <option value="">
                        {filters.groupId != null ? 'All in group' : 'All Restaurants'}
                      </option>
                      {visibleRestaurants.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                <select
                  value={filters.level || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      level: (e.target.value || undefined) as PosErrorLevel | undefined,
                    })
                  }
                  className="input w-full"
                >
                  <option value="">All Levels</option>
                  <option value="ERROR">ERROR</option>
                  <option value="FATAL">FATAL</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.resolved ?? 'all'}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      resolved: e.target.value as 'all' | 'unresolved' | 'resolved',
                    })
                  }
                  className="input w-full"
                >
                  <option value="all">All</option>
                  <option value="unresolved">Unresolved only</option>
                  <option value="resolved">Resolved only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terminal ID</label>
                <input
                  type="text"
                  value={filters.terminalId ?? ''}
                  onChange={(e) =>
                    setFilters({ ...filters, terminalId: e.target.value || undefined })
                  }
                  placeholder="e.g. POS-3"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Error Class</label>
                <input
                  type="text"
                  value={filters.errorClass ?? ''}
                  onChange={(e) =>
                    setFilters({ ...filters, errorClass: e.target.value || undefined })
                  }
                  placeholder="e.g. NullPointerException"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.fromDate ?? ''}
                  onChange={(e) =>
                    setFilters({ ...filters, fromDate: e.target.value || undefined })
                  }
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.toDate ?? ''}
                  onChange={(e) =>
                    setFilters({ ...filters, toDate: e.target.value || undefined })
                  }
                  className="input w-full"
                />
              </div>
            </div>

            {activeFilterCount > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => setFilters({ resolved: 'unresolved' })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-3 text-sm text-gray-600">
          {searchQuery
            ? `${filteredData?.length ?? 0} of ${errorsPage?.content?.length ?? 0} entries on this page match "${searchQuery}"`
            : `Showing ${errorsPage?.content?.length ?? 0} of ${errorsPage?.totalElements ?? 0} entries (page ${errorsPageIndex + 1} of ${Math.max(errorsPage?.totalPages ?? 1, 1)})`}
          {searchQuery && (
            <span className="block text-xs text-gray-400 mt-0.5">
              Search only matches entries already loaded on this page -- use the filters above to narrow a larger result set.
            </span>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-3">Failed to load POS errors</p>
            <button onClick={() => refetch()} className="btn btn-outline h-10">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        ) : (
          <DataTable<TerminalErrorEntry>
            data={filteredData || []}
            columns={columns}
            keyExtractor={(entry) => String(entry.id)}
            showPagination={false}
            emptyMessage="No POS errors match the current filters"
          />
        )}
      </div>

      {/* Server-side pagination -- each fetch is already exactly one page's
          worth of results, unlike the old hardcoded page:0,size:100 that made
          anything past the 100th most recent matching entry unreachable (H3). */}
      {!isLoading && !isError && (errorsPage?.totalPages ?? 0) > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {errorsPageIndex + 1} of {errorsPage?.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setErrorsPageIndex((p) => Math.max(0, p - 1))}
              disabled={errorsPageIndex === 0}
              className="btn btn-outline h-9 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setErrorsPageIndex((p) => Math.min((errorsPage?.totalPages ?? 1) - 1, p + 1))
              }
              disabled={errorsPageIndex >= (errorsPage?.totalPages ?? 1) - 1}
              className="btn btn-outline h-9 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selected && (
        <ErrorDetailModal
          entry={selected}
          restaurantName={restaurantNameById.get(selected.restaurantId)}
          onClose={() => {
            resolveMutation.reset();
            setSelected(null);
          }}
          onResolve={() => resolveMutation.mutate(selected.id)}
          resolving={resolveMutation.isPending}
          resolveError={
            resolveMutation.isError
              ? (resolveMutation.error as any)?.response?.data?.message || 'Failed to mark resolved'
              : null
          }
          canResolve={canManageErrors}
        />
      )}
    </div>
  );
}

interface ErrorDetailModalProps {
  entry: TerminalErrorEntry;
  restaurantName?: string;
  onClose: () => void;
  onResolve: () => void;
  resolving: boolean;
  resolveError: string | null;
  canResolve: boolean;
}

function ErrorDetailModal({
  entry,
  restaurantName,
  onClose,
  onResolve,
  resolving,
  resolveError,
  canResolve,
}: ErrorDetailModalProps) {
  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalHeader onClose={onClose}>
        {entry.errorClass ?? 'Error'} · {entry.terminalId}
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Level:</span>
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs ${levelStyle[entry.level]}`}
              >
                {entry.level}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Occurred:</span>
              <span className="ml-2 text-gray-900">
                {new Date(entry.occurredAt).toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Restaurant:</span>
              <span className="ml-2 text-gray-900">
                {restaurantName ?? `#${entry.restaurantId}`}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Terminal:</span>
              <span className="ml-2 text-gray-900 font-mono">{entry.terminalId}</span>
            </div>
            {entry.threadName && (
              <div>
                <span className="text-gray-500">Thread:</span>
                <span className="ml-2 text-gray-900 font-mono text-xs">{entry.threadName}</span>
              </div>
            )}
            {entry.loggerClass && (
              <div>
                <span className="text-gray-500">Logger:</span>
                <span className="ml-2 text-gray-900 font-mono text-xs">{entry.loggerClass}</span>
              </div>
            )}
          </div>

          {entry.message && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">MESSAGE</h4>
              <pre className="text-sm whitespace-pre-wrap break-words text-gray-800">
                {entry.message}
              </pre>
            </div>
          )}

          {entry.stackTrace && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">STACK TRACE</h4>
              <pre className="bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-auto max-h-80">
                {entry.stackTrace}
              </pre>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">ENVIRONMENT</h4>
            <div className="space-y-1 text-sm">
              {entry.posAppVersion && (
                <div className="flex">
                  <span className="text-gray-500 w-32">POS app version:</span>
                  <span className="text-gray-900">{entry.posAppVersion}</span>
                </div>
              )}
              {entry.javaVersion && (
                <div className="flex">
                  <span className="text-gray-500 w-32">Java version:</span>
                  <span className="text-gray-900">{entry.javaVersion}</span>
                </div>
              )}
              {entry.osInfo && (
                <div className="flex">
                  <span className="text-gray-500 w-32">OS:</span>
                  <span className="text-gray-900">{entry.osInfo}</span>
                </div>
              )}
              {entry.posUserId != null && (
                <div className="flex">
                  <span className="text-gray-500 w-32">POS user id:</span>
                  <span className="text-gray-900">#{entry.posUserId}</span>
                </div>
              )}
            </div>
          </div>

          {entry.metadata && Object.keys(entry.metadata).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">METADATA</h4>
              <pre className="bg-white border border-gray-200 rounded p-2 text-xs font-mono overflow-auto max-h-32">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </div>
          )}

          {entry.resolved && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
              Resolved
              {entry.resolvedAt && ` · ${new Date(entry.resolvedAt).toLocaleString()}`}
              {entry.resolvedBy != null && ` · by user #${entry.resolvedBy}`}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {resolveError && (
          <span className="text-sm text-red-600 mr-auto">{resolveError}</span>
        )}
        {!entry.resolved && canResolve && (
          <button
            onClick={onResolve}
            disabled={resolving}
            className="btn btn-primary h-10 mr-2"
          >
            {resolving ? 'Resolving…' : 'Mark resolved'}
          </button>
        )}
        <button onClick={onClose} className="btn btn-outline h-10">
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}
